import type { SearchResponse } from "@/features/search/api/queries";
import type { Facet } from "@/features/search/api/types";
import { formatResultCount } from "@/features/search/utils/count-placeholder.utils";

/**
 * WHO-5756: head title and meta description for the listing pages Strapi has no SEO copy for.
 *
 * 21,632 listing pages per locale are in the sitemaps and about 3% have an editor-written
 * `seo.title`. The rest took the service's page name as the whole title ("Midj Stoelen / fauteuils
 * | Whoppah") and, on the 2026-09-09 sample, 22 of 25 had no meta description at all. These
 * builders put the searcher's own word for pre-owned in the title (the words the site already
 * ranks best on: "tweedehands" NL, "gebraucht" DE) plus the live result count, and write one
 * factual description sentence from the same response the grid renders.
 *
 * No price is quoted: sold products stay in the index on purpose, so the price facet's minimum
 * can be a price nobody can buy at.
 */

/** Facts the loader reads off the search response; small enough to pass as page props. */
export type ListingHeadFacts = {
  /** The result count the pagination shows; `null` when the search failed. */
  total: number | null;
  /** Up to three brand names on the page, most listings first; empty on a brand page. */
  topBrands: string[];
  /** The page is filtered to a brand or designer (`/brands/midj/...`, `/designers/mogensen`). */
  filteredByMaker: boolean;
  /** The first page's listings, for the ItemList markup: name and service path. */
  items: { name: string; path: string }[];
};

const TOP_BRAND_LIMIT = 3;

/** Enough for the list to describe the page; the full grid is already in the dehydrated cache. */
const ITEM_LIST_LIMIT = 24;

/**
 * A page name that already carries a pre-owned or era word ("Vintage Kroonluchters") keeps its
 * own wording; stacking "Tweedehands Vintage ..." reads as spam.
 */
const PRE_OWNED_WORDS =
  /\b(vintage|antiek|antik|antique|antico|antiguo|retro|tweedehands|second[- ]?hand|gebraucht|occasion|seconda mano|segunda mano|usat[oiae]|pre-?owned|used)\b/i;

type LocaleWords = {
  title: (name: string, count: string | null) => string;
  description: (name: string, count: string, brands: string | null) => string;
  /** WHO-5760: the visible one-line intro on a page without Strapi copy. */
  intro: (name: string, count: string, brands: string | null) => string;
  and: string;
};

const withCount = (text: string, count: string | null) =>
  count ? `${text} (${count})` : text;

const WORDS: Record<string, LocaleWords> = {
  en: {
    title: (name, count) => withCount(`Second-hand ${name}`, count),
    description: (name, count, brands) =>
      `${count} second-hand ${name} on Whoppah${
        brands ? `, from brands such as ${brands}` : ""
      }. Curated pre-owned design, delivered across Europe.`,
    intro: (name, count, brands) =>
      `${count} second-hand ${name}${
        brands ? ` from ${brands} and more` : ""
      }, each one checked by our curators and shipped across Europe.`,
    and: "and",
  },
  nl: {
    title: (name, count) => withCount(`Tweedehands ${name}`, count),
    description: (name, count, brands) =>
      `${count} tweedehands ${name} op Whoppah${
        brands ? `, van merken als ${brands}` : ""
      }. Gecureerd design, geleverd in heel Europa.`,
    intro: (name, count, brands) =>
      `${count} tweedehands ${name}${
        brands ? ` van onder meer ${brands}` : ""
      }, elk stuk gecheckt door onze curatoren en verzonden in heel Europa.`,
    and: "en",
  },
  de: {
    title: (name, count) => withCount(`${name} gebraucht`, count),
    description: (name, count, brands) =>
      `${count} ${name} gebraucht bei Whoppah${
        brands ? `, von Marken wie ${brands}` : ""
      }. Kuratiertes Second-Hand-Design, geliefert in ganz Europa.`,
    intro: (name, count, brands) =>
      `${count} ${name} gebraucht${
        brands ? `, unter anderem von ${brands}` : ""
      }, jedes Stück von unseren Kuratoren geprüft und in ganz Europa geliefert.`,
    and: "und",
  },
  fr: {
    title: (name, count) => withCount(`${name} d'occasion`, count),
    description: (name, count, brands) =>
      `${count} ${name} d'occasion sur Whoppah${
        brands ? `, de marques comme ${brands}` : ""
      }. Design de seconde main sélectionné, livré dans toute l'Europe.`,
    intro: (name, count, brands) =>
      `${count} ${name} d'occasion${
        brands ? `, notamment de ${brands}` : ""
      }, chaque pièce vérifiée par nos curateurs et livrée dans toute l'Europe.`,
    and: "et",
  },
  it: {
    title: (name, count) => withCount(`${name} di seconda mano`, count),
    description: (name, count, brands) =>
      `${count} ${name} di seconda mano su Whoppah${
        brands ? `, di marchi come ${brands}` : ""
      }. Design usato selezionato, consegnato in tutta Europa.`,
    intro: (name, count, brands) =>
      `${count} ${name} di seconda mano${
        brands ? `, tra cui ${brands}` : ""
      }, ogni pezzo verificato dai nostri curatori e spedito in tutta Europa.`,
    and: "e",
  },
  es: {
    title: (name, count) => withCount(`${name} de segunda mano`, count),
    description: (name, count, brands) =>
      `${count} ${name} de segunda mano en Whoppah${
        brands ? `, de marcas como ${brands}` : ""
      }. Diseño de segunda mano seleccionado, con envío a toda Europa.`,
    intro: (name, count, brands) =>
      `${count} ${name} de segunda mano${
        brands ? `, entre ellas de ${brands}` : ""
      }, cada pieza revisada por nuestros curadores y enviada a toda Europa.`,
    and: "y",
  },
};

const wordsFor = (locale: string | undefined): LocaleWords =>
  WORDS[locale ?? "en"] ?? (WORDS.en as LocaleWords);

const listOf = (items: string[], and: string): string | null => {
  if (items.length === 0) {
    return null;
  }

  if (items.length === 1) {
    return items[0] ?? null;
  }

  return `${items.slice(0, -1).join(", ")} ${and} ${items[items.length - 1]}`;
};

const hasCount = (total: number | null): total is number =>
  typeof total === "number" && Number.isFinite(total) && total > 0;

/** Title without the " | Whoppah" suffix, which `<SEO>` adds. */
export const buildListingHeadTitle = (
  name: string,
  facts: ListingHeadFacts,
  locale: string | undefined
): string => {
  if (PRE_OWNED_WORDS.test(name)) {
    return name;
  }

  const count = hasCount(facts.total)
    ? formatResultCount(facts.total, locale)
    : null;

  return wordsFor(locale).title(name, count);
};

/** `null` when there is no count: a description without one would be a slogan, not a fact. */
export const buildListingHeadDescription = (
  name: string,
  facts: ListingHeadFacts,
  locale: string | undefined
): string | null => {
  if (!hasCount(facts.total)) {
    return null;
  }

  const words = wordsFor(locale);
  /*
   * Mid-sentence a category is a common noun ("103.732 muebles"). A brand or designer page starts
   * with a proper noun, and German capitalises nouns, so those keep the name as it is.
   */
  const sentenceName =
    facts.filteredByMaker || locale === "de" ? name : name.toLowerCase();

  return words.description(
    sentenceName,
    formatResultCount(facts.total, locale),
    listOf(facts.topBrands, words.and)
  );
};

/**
 * WHO-5760: the one visible sentence on a listing page with no Strapi intro, from the same facts
 * as the meta description and under the same rules: no sentence without a live count, no price
 * (sold products stay in the index), and on a brand or designer page no other brand named, since
 * that page's brand facet lists alternatives, not items on the page (`topBrands` is empty there).
 */
export const buildListingIntro = (
  name: string,
  facts: ListingHeadFacts,
  locale: string | undefined
): string | null => {
  if (!hasCount(facts.total)) {
    return null;
  }

  const words = wordsFor(locale);
  const sentenceName =
    facts.filteredByMaker || locale === "de" ? name : name.toLowerCase();

  return words.intro(
    sentenceName,
    formatResultCount(facts.total, locale),
    facts.filteredByMaker ? null : listOf(facts.topBrands, words.and)
  );
};

/**
 * Service descriptions under this length are stubs ("Tot 80% korting bij Whoppah" on
 * `/nl/merken/bretz`), not copy; a generated sentence says more. Longer ones are the page's own
 * intro prose and stay.
 */
const MIN_REAL_DESCRIPTION_LENGTH = 80;

export const isStubDescription = (text: string | null | undefined): boolean =>
  !text || text.trim().length < MIN_REAL_DESCRIPTION_LENGTH;

type TermsFacet = Extract<Facet, { type: "terms" }>;

const MAKER_FACET_KEYS = new Set(["brand", "designer"]);

const isTermsFacet = (facet: Facet): facet is TermsFacet =>
  facet.type === "terms";

/**
 * Read the facts off the search response. On a page filtered to a brand or designer (a locked or
 * selected value, as on `/brands/vitra`) no brands are named at all: the facet's other values are
 * the brands one could switch to, not listings on the page (the 2026-09-23 preview named Leolux,
 * Vitra and Artifort on the Midj page).
 */
export const toListingHeadFacts = (
  response:
    | Pick<SearchResponse, "pageInfo" | "facets" | "data">
    | null
    | undefined
): ListingHeadFacts => {
  const termsFacets = (response?.facets ?? []).filter(isTermsFacet);
  const filteredByMaker = termsFacets.some(
    facet =>
      MAKER_FACET_KEYS.has(facet.key) &&
      facet.values.some(value => value.locked || value.selected)
  );
  const brandFacet = termsFacets.find(facet => facet.key === "brand");
  const topBrands = (filteredByMaker ? [] : brandFacet?.values ?? [])
    .filter(value => !value.locked && !value.selected && value.label)
    .sort((first, second) => second.count - first.count)
    .slice(0, TOP_BRAND_LIMIT)
    .map(value => value.label as string);

  const items = (response?.data ?? [])
    .slice(0, ITEM_LIST_LIMIT)
    .map(product => ({
      name: product.title,
      path: `products/${product.slug}`,
    }));

  return {
    total: response?.pageInfo.total ?? null,
    topBrands,
    filteredByMaker,
    items,
  };
};

/**
 * ItemList of the page's listings, next to the CollectionPage and FAQ markup: URLs and names
 * only, no nested Product, which Google reserves for the product page itself.
 */
export const buildListingItemListSchema = (
  items: { name: string; url: string }[],
  pageUrl: string
) =>
  items.length === 0
    ? null
    : {
        "@context": "https://schema.org",
        "@type": "ItemList",
        "@id": `${pageUrl}#listings`,
        numberOfItems: items.length,
        itemListElement: items.map((item, position) => ({
          "@type": "ListItem",
          position: position + 1,
          url: item.url,
          name: item.name,
        })),
      };
